/* eslint-disable camelcase */
import { useCallback, useLayoutEffect, useState } from 'react';

import { DEFAULT_BASE_FRAME_SHAPE, LOCAL_STORAGE, PRODUCT_TYPES } from '@utils/enums';
import { getRandomArrayItem } from '@utils/js-utils';
import { fetchRecommendations } from '@utils/shopify-helpers';

/**
 * Shopify Recommendations
 *
 * Fetches Shopify recommendations and processes the returned product data.
 * Generates type-specific product variant objects that are randomized for all products *except*
 * Top Frames. Top Frame recommendations are matched to the active (all PDPs) or selected
 * base frame shape (Base Frame and Top Frame PDPs only).
 *
 * @param {object} { productId {str || num}, rootUrl {str}, selectedBaseFrame {str || num}}
 * @returns {array} recommendationProducts
 */
const useShopifyRecommendations = ({ activeTopCollections, productId, productTitle, rootUrl = '/', selectedBaseFrame }) => {
	const [recommendationProducts, setRecommendationProducts] = useState([]);

	const determineBaseFrameDefault = frameShape => {
		let baseFrameDefault = frameShape ?? DEFAULT_BASE_FRAME_SHAPE;

		if (frameShape) baseFrameDefault = frameShape;
		else localStorage.getItem(LOCAL_STORAGE.ACTIVE_FRAME_SHAPE) ?? DEFAULT_BASE_FRAME_SHAPE;

		return baseFrameDefault;
	};

	const addAccessoryCaption = (title, variantTitle) => {
		switch (title) {
			case 'Cleaning Cloths':
				return ' Pack of 3 eco-friendly cloths ';

			case 'Glasses Case':
				return ' Holds a Base Frame & two Top Frames ';

			case 'Cleaning Kit':
				return ' The all-in-one kit includes lens cleaning spray, a cloth, & a carrying kit';

			case 'Top Frame Case':
				return `in ${variantTitle} - holds two Top Frames`;

			case 'Wall Hanger':
				return `${variantTitle === 'Perry' ? 'featuring Perry, our mascot' : `in ${variantTitle}`}`;

			default:
				return `in ${variantTitle}`;
		}
	};

	const processRecommendationProducts = ({ products, frameShape }) => {
		return products.map(recProduct => {
			const { featured_image, handle, id, tags, title, type, url, variants } = recProduct;

			if (productTitle === title) return;

			let caption = '';
			let image = '';
			let newUrl = '';

			if (type.includes(PRODUCT_TYPES.TOP_FRAME)) {
				const baseFrameDefault = determineBaseFrameDefault(frameShape);
				const frameShapeVariant = variants.find(v => v.option1 === baseFrameDefault) ?? variants[0];

				caption = `for The ${baseFrameDefault}`;
				image = frameShapeVariant.featured_image.src;
				newUrl = `${rootUrl === '/' ? rootUrl : rootUrl + '/'}products/${handle}?frameShape=${baseFrameDefault}`;

				return {
					...frameShapeVariant,
					caption,
					handle,
					image,
					tags,
					title,
					frame_shape: frameShapeVariant.title,
					product_id: id,
					collection: (activeTopCollections && activeTopCollections[type]) ?? { type },
					type: 'TOP_FRAME',
					url: newUrl,
				};
			}

			const randomizedVariant = getRandomArrayItem(variants);

			if (type === PRODUCT_TYPES.BASE_FRAME) {
				const color = randomizedVariant.option1;
				if (color === 'Gray Tortoise') return;

				caption = `in ${color}`;
				image = randomizedVariant?.featured_image?.src ?? '';
				newUrl = `${url}&base_color=${color}&view=base_frame`;

				return {
					...randomizedVariant,
					caption,
					image,
					title,
					type,
					url: newUrl,
				};
			}

			if (type === PRODUCT_TYPES.ACCESSORY) {
				const newLocal = randomizedVariant?.featured_image?.src ?? featured_image;
				return {
					...recProduct,
					product_id: id,
					...randomizedVariant,
					title,
					type,
					caption: addAccessoryCaption(title, randomizedVariant.title),
					image: newLocal,
				};
			}
		});
	};

	const updateRecommendations = useCallback(async () => {
		const recData = await fetchRecommendations({ productId, rootUrl });
		const result = processRecommendationProducts({ products: recData, frameShape: selectedBaseFrame });

		setRecommendationProducts(result);
	}, []);

	useLayoutEffect(() => {
		updateRecommendations();
	}, [updateRecommendations]);

	return recommendationProducts;
};

export default useShopifyRecommendations;
